import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["postGrid", "postItem"]

  connect() {
    this.cardClickHandler = this.openCard.bind(this)
    this.cardKeydownHandler = this.openCardWithKeyboard.bind(this)
    this.element.addEventListener('click', this.cardClickHandler)
    this.element.addEventListener('keydown', this.cardKeydownHandler)

    this.setupPinterestLayout()
    this.setupResponsiveColumns()
    
    // Initialize existing posts with fade-in effect
    this.initializeExistingPosts()
  }

  disconnect() {
    if (this.cardClickHandler) {
      this.element.removeEventListener('click', this.cardClickHandler)
    }
    if (this.cardKeydownHandler) {
      this.element.removeEventListener('keydown', this.cardKeydownHandler)
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
  }

  openCard(event) {
    if (event.target.closest('a, button')) return

    const card = event.target.closest('.post-pin[data-card-url]')
    if (!card) return

    window.location.href = card.dataset.cardUrl
  }

  openCardWithKeyboard(event) {
    if (!['Enter', ' '].includes(event.key)) return
    if (event.target.closest('a, button')) return

    const card = event.target.closest('.post-pin[data-card-url]')
    if (!card) return

    event.preventDefault()
    window.location.href = card.dataset.cardUrl
  }

  setupPinterestLayout() {
    // Pinterest uses CSS columns for masonry layout
    this.adjustColumns()
  }
  
  initializeExistingPosts() {
    // Wait for images to load, then show posts with masonry layout ready
    const posts = this.element.querySelectorAll('.post-pin')
    let loadedCount = 0
    const totalPosts = posts.length
    
    if (totalPosts === 0) {
      return
    }
    
    const checkAllImagesLoaded = () => {
      loadedCount++
      
      if (loadedCount >= totalPosts) {
        // All images loaded, wait a bit for masonry to settle, then fade in
        setTimeout(() => {
          posts.forEach((post, index) => {
            setTimeout(() => {
              post.classList.add('masonry-ready')
              post.style.opacity = '1'
              post.style.transform = 'translateY(0)'
            }, index * 50) // Stagger the fade-in slightly
          })
        }, 100)
      }
    }
    
    // Initialize posts with fade-in styles
    posts.forEach(post => {
      post.style.opacity = '0'
      post.style.transform = 'translateY(20px)'
      post.style.transition = 'opacity 0.6s ease, transform 0.6s ease'
    })
    
    // Force fade-in as fallback after 2 seconds
    setTimeout(() => {
      if (loadedCount < totalPosts) {
        posts.forEach(post => {
          post.classList.add('masonry-ready')
          post.style.opacity = '1'
          post.style.transform = 'translateY(0)'
        })
      }
    }, 2000)
    
    posts.forEach((post, photoIndex) => {
      const img = post.querySelector('img')
      if (img) {
        if (img.complete && img.naturalHeight > 0) {
          // Image already loaded
          checkAllImagesLoaded()
        } else {
          // Wait for image to load
          img.addEventListener('load', () => {
            checkAllImagesLoaded()
          }, { once: true })
          img.addEventListener('error', () => {
            checkAllImagesLoaded()
          }, { once: true })
        }
      } else {
        checkAllImagesLoaded()
      }
    })
  }

  setupResponsiveColumns() {
    // Adjust column count based on screen size
    this.resizeHandler = () => {
      this.adjustColumns()
    }
    window.addEventListener('resize', this.resizeHandler)
  }

  adjustColumns() {
    const grid = this.element
    const width = window.innerWidth
    
    let columns = 4 // Desktop default
    let columnGap = '16px'
    
    if (width < 640) {
      columns = 1 // Mobile - single column for posts
      columnGap = '16px'
    } else if (width < 768) {
      columns = 2 // Small tablet
      columnGap = '16px'
    } else if (width < 1024) {
      columns = 3 // Large tablet
      columnGap = '16px'
    } else if (width < 1280) {
      columns = 4 // Desktop
      columnGap = '16px'
    } else {
      columns = 4 // Large desktop - keep at 4 for posts
      columnGap = '20px'
    }
    
    grid.style.columns = columns
    grid.style.columnGap = columnGap
  }

  // Handle image loading errors
  imageError(event) {
    const img = event.target
    const placeholder = img.closest('.post-pin')
    
    // Show placeholder or fade out
    placeholder.style.opacity = '0.7'
    img.alt = 'Image could not be loaded'
  }

  // Handle successful image load
  imageLoaded(event) {
    const item = event.target.closest('.post-pin')
    if (item) {
      item.classList.remove('loading')
    }
  }
}
